// @ts-nocheck
import React from 'react';
import { momentTz } from '../dates';

import isString from 'lodash/isString';
import { Chip } from '@mui/material';
import Link from '@mui/material/Link';

import { isValueEmpty } from '../core';
import { findUsernameByUserId, findGroupNameByGroupId } from '../users';
import { isHealthStatusObject } from '../validation';
import {
  DATE_FORMAT,
  DD_MM_YYYY_DATE_FORMAT,
  YYYY_MM_DD_DATE_FORMAT,
  USERS_WITH_DRAFT,
  ATTRIBUTE_DATA_TYPE_HTML,
  EMPTY_OBJECT,
} from '../../const/CommonConst';
import ViewChart from '../../components/Common/AttributesForm/ViewAttributes/ViewChart';
import ViewTable from '../../components/Common/AttributesForm/ViewAttributes/ViewTable';
import ViewAdvancedTable from '../../components/Common/AttributesForm/ViewAttributes/ViewAdvancedTable';
import ViewChecklist from '../../components/Common/AttributesForm/ViewAttributes/ViewChecklist';
import ViewDocumentAttribute from '../../components/Common/AttributesForm/ViewAttributes/ViewDocumentAttribute';
import ViewParagraph from '../../components/Common/AttributesForm/ViewAttributes/ViewParagraph';
import ViewTextFieldExtended from '../../components/Common/AttributesForm/ViewAttributes/ViewTextFieldExtended';
import ViewCanvas from '../../components/Common/AttributesForm/ViewAttributes/ViewCanvas';
import SegmentsAttribute from '../../components/Common/AttributesForm/FieldComponents/SegmentsAttribute';
import FieldChangeOtherDetails from '../../components/Common/AttributesForm/FieldComponents/FieldChangeOtherDetails';
import IssueTypeComponent from '../../components/Common/AttributesForm/FieldComponents/IssueTypeComponent';
import EntityHealthIndicator from '../../components/Common/ModelInventoryAndAssociation/EntityHealthIndicator';
import AlertHealthIndicator from '../../components/Common/ModelInventoryAndAssociation/AlertHealthIndicator';
import { LazyHtmlAttributePreviewLink } from '../../components/Common/AttributesForm/HtmlPreviewDialogLink';
import {
  AssociationFieldTableViewComponent,
  AssociationValueLink,
  InventoryViewAttribute,
} from '../../components/Common/AttributesForm/FieldComponents';

/** @param {object|null|undefined} obj - e.g. attribute or form values with `data_type` */
export const isHtmlAttributeType = (obj) =>
  obj?.data_type === ATTRIBUTE_DATA_TYPE_HTML;

/**
 * Read-only common attributes (attribute_origin: 'common' & is_editable: false)
 * must not be selectable in the automation "Common Attributes" pickers.
 * Use as an exclude-predicate, e.g. attributes.filter((a) => !isReadOnlyCommonAttribute(a)).
 * @param {object|null|undefined} attribute
 * @returns {boolean} true when the attribute is a read-only common attribute
 */
export const isReadOnlyCommonAttribute = (attribute) =>
  attribute?.attribute_origin === 'common' && attribute?.is_editable === false;

/**
 * Case-insensitive substring match for list/search filters.
 * Coerces with String() so API fields that are numbers do not throw on .toLowerCase().
 * @param {*} value
 * @param {string} searchLower - lowercased search query
 */
export const taskFieldMatchesSearch = (value, searchLower) =>
  String(value ?? '')
    .toLowerCase()
    .includes(searchLower);

/** Recursively checks if a TipTap doc object has any non-empty text. */
const hasTipTapText = (node) => {
  if (!node) return false;
  if (node.type === 'text') return !!node.text?.trim();
  return (node.content || []).some(hasTipTapText);
};

/**
 * Returns true when rich-text content has no visible text.
 * Handles TipTap JSON (new format), DraftJS JSON (legacy), HTML strings, and empty strings.
 */
export const isRichContentEmpty = (content) => {
  if (!content) return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      // TipTap native JSON
      if (parsed?.type === 'doc') return !hasTipTapText(parsed);
      // Legacy DraftJS JSON format
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks.every((b) => !b.text?.trim());
      }
    } catch {
      // plain HTML
    }
    return content.replace(/<[^>]*>/g, '').trim() === '';
  }
  if (typeof content === 'object') {
    if (content.type === 'doc') return !hasTipTapText(content);
    if (Array.isArray(content.blocks))
      return content.blocks.every((b) => !b.text?.trim());
  }
  return true;
};

export function isArrayofObjects(arr) {
  if (!Array.isArray(arr)) {
    return false;
  }
  if (!arr.length) {
    return false;
  }
  return arr?.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      !('label' in item && 'value' in item)
  );
}

/**
 * Check if a value has the structure { columns: [...], data: [...] }, This is for checking the table datatype value in the logs as we do not have datatype there to detect
 * @param {any} value - The value to check
 * @returns {boolean} - True if value has columns and data properties with correct types
 */
export function isTableDataStructure(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const hasColumns = 'columns' in value && Array.isArray(value.columns);
  const hasData = 'data' in value && Array.isArray(value.data);

  return hasColumns && hasData;
}

/**
 * @function isDateValid
 * @description Checks if a date string is valid. Supports both UTC and "yyyy-mm-dd" formats.
 *
 * @param {string} dateString - The date string to validate.
 * @returns {boolean} `true` if the date string is valid, `false` otherwise.
 */
export const isDateValid = (dateString) => {
  const FORMAT = [
    DD_MM_YYYY_DATE_FORMAT,
    DATE_FORMAT,
    YYYY_MM_DD_DATE_FORMAT,
    momentTz.ISO_8601,
  ];
  if (!Number.isNaN(Number(dateString))) {
    return false;
  }
  if (!isString(dateString)) {
    return false;
  }
  return momentTz(dateString, FORMAT, true).isValid();
};

/**
 * Checks if a given value is present in a string with comma-separated options of drop-down options.
 *
 * @param {string} value - The value to check.
 * @param {string} dropDownOptions - A string with comma-separated options of drop-down options.
 * @returns {boolean} `true` if the value is present in the options or if the value is undefined, `false` otherwise.
 *
 * @example
 * // Example usage:
 * const value = 'abc';
 * const options = 'abc,def,ghi';
 * const result = isValueInDropDownOptions(value, options); // Returns true
 *
 * // Note: The `dropDownOptions` string is split by commas to ensure accurate matching.
 * // This is done to handle cases where default values contain a trailing comma (e.g., 'abc,').
 */
export const isValueInDropDownOptions = (value, dropDownOptions) => {
  return !(
    dropDownOptions &&
    value &&
    !dropDownOptions
      ?.split(',')
      .map((item) => item?.trim())
      .filter((item) => item?.length > 0)
      .includes(value?.trim())
  );
};

export const getAttributeValueColors = (theme) => ({
  Manual: theme?.palette?.text?.manual,
  Automation: theme?.palette?.text?.automation,
  Calculated: theme?.palette?.text?.calculated,
  Overridden: theme?.palette?.text?.overridden,
  Default: theme?.palette?.text?.default,
});

const dontCheckValuesFor = new Set(['IssueType', 'InventoryView', 'HTML']);

/**
 * Format an attribute value for display based on its data type
 */
export const formatAttributeValue = (
  value,
  attributeInfo,
  hasDraftChanges = false,
  entityId = null,
  entityDetailsFormikForm = EMPTY_OBJECT,
  usersList,
  groupList,
  entityType = null,
  associationTypes = EMPTY_OBJECT,
  isViewMode = false,
  configColumn = false,
  entityData = EMPTY_OBJECT
) => {
  const isShowAssociationAsTable = attributeInfo?.show_as_table;
  const dataType = attributeInfo?.data_type;
  const resolvedEntityId =
    entityId || entityData?.entity_id || entityData?.entityId;

  if (attributeInfo?.name === 'entity_health' || isHealthStatusObject(value)) {
    const healthObj = isHealthStatusObject(value)
      ? value
      : attributeInfo?.value;

    if (healthObj?.label) {
      return <EntityHealthIndicator health={healthObj} />;
    }

    const healthFlag = entityData?.Health;
    if (healthFlag) {
      return <AlertHealthIndicator flag={healthFlag} />;
    }

    return '-';
  }

  if (
    isValueEmpty(value) &&
    !dontCheckValuesFor.has(dataType) &&
    !isShowAssociationAsTable
  ) {
    return '-';
  }

  // Show template version as a chip (single value)
  if (attributeInfo?.name === 'template_version_number') {
    return (
      <Chip
        label={String(value)}
        title={String(value)}
        size="small"
        className="sm-chip"
        sx={{
          minHeight: '22px',
          fontSize: '12px',
          fontWeight: '600',
          maxWidth: '100%',
        }}
        color="primary"
        variant="outlined"
      />
    );
  }

  let displayValue = value;

  const inputControlType = attributeInfo?.input_control_type;

  // First check data type
  switch (dataType) {
    case 'Character':
      // For Character data type, check input control type
      switch (inputControlType) {
        case 'TEXTFIELD':
          displayValue = value.toString();
          break;
        case 'SINGLE_SELECT':
          if (attributeInfo?.name === USERS_WITH_DRAFT) {
            displayValue =
              value === 'True' || hasDraftChanges ? (
                <Chip
                  label="Draft"
                  title="Draft"
                  size="small"
                  className="sm-chip md-chip"
                  sx={{
                    minHeight: '22px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                  color="primary"
                />
              ) : (
                '-'
              );
          } else {
            displayValue = value || '-';
          }
          break;
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value.map((v) => v?.value ?? v).join(', ');
          } else {
            displayValue = '-';
          }
          break;
        case 'TEXTFIELD_EXTENDED':
          displayValue = value ? (
            <ViewTextFieldExtended value={value} attribute={attributeInfo} />
          ) : (
            '-'
          );
          break;
        case 'Canvas':
          displayValue = value ? (
            <ViewCanvas value={value} attribute={attributeInfo} />
          ) : (
            '-'
          );
          break;
        default:
          // Default handling for Character
          displayValue = isString(value) ? value : String(value);
      }
      break;

    case 'Integer':
      if (value !== '') {
        displayValue = Number(value).toLocaleString();
      }
      break;

    case 'Decimal':
      if (value !== '') {
        displayValue = Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        });
      }
      break;

    case 'Date':
      if (value) {
        const momentDate = momentTz(value);
        if (momentDate.isValid()) {
          displayValue = momentDate.format(DATE_FORMAT);
        } else {
          displayValue = value;
        }
      }
      break;

    case 'MultiSelect':
      if (Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      break;

    case 'Table':
      if (Array.isArray(value?.data)) {
        displayValue = value ? (
          <ViewTable value={value} attribute={attributeInfo} />
        ) : (
          '-'
        );
      }
      break;

    case 'AdvancedTable':
      displayValue = value ? (
        <ViewAdvancedTable
          value={value}
          attribute={attributeInfo}
          entityId={entityId}
          entityType={entityType}
        />
      ) : (
        '-'
      );
      break;

    case 'Chart':
      displayValue = value ? (
        <ViewChart value={value} attribute={attributeInfo} />
      ) : (
        '-'
      );
      break;

    case 'Checklist':
      displayValue = value ? (
        <ViewChecklist
          value={value}
          attribute={attributeInfo}
          entityId={entityId}
          entityType={entityType}
        />
      ) : (
        '-'
      );
      break;
    case 'Document':
      displayValue = value ? (
        <ViewDocumentAttribute
          documentDetails={value}
          _isViewMode={isViewMode}
          attribute={attributeInfo}
          configColumn={configColumn}
          entityId={entityId}
          entityType={entityType}
        />
      ) : (
        '-'
      );
      break;
    case 'Paragraph': {
      // if title & justification both not present then in view mode instead of showing blank data updated here it will show "-"
      const hasParagraphValue =
        value && (value?.title?.trim() || value?.description?.trim());

      displayValue = hasParagraphValue ? (
        <ViewParagraph value={value} attribute={attributeInfo} />
      ) : (
        '-'
      );
      break;
    }
    case ATTRIBUTE_DATA_TYPE_HTML:
      displayValue = (
        <LazyHtmlAttributePreviewLink
          attribute={attributeInfo}
          entityId={resolvedEntityId}
          showPdfDownload
        />
      );
      break;

    case 'Segments':
      displayValue = value ? (
        <SegmentsAttribute
          value={value}
          attribute={attributeInfo}
          viewOnly={true}
          entityData={{
            entity_id: entityId,
            entity_type: attributeInfo?.entityType || 'ModelInventory',
          }}
          entityDetailsFormikForm={entityDetailsFormikForm}
        />
      ) : (
        '-'
      );
      break;

    case 'IssueType':
      displayValue = (
        <IssueTypeComponent
          attributeInfo={attributeInfo}
          associationTypes={associationTypes}
        />
      );
      break;
    case 'InventoryView': {
      displayValue = (
        <InventoryViewAttribute
          mode={'view'}
          value={value}
          attribute={attributeInfo}
          entityDetailsFormikForm={entityDetailsFormikForm}
          entityId={resolvedEntityId}
        />
      );
      break;
    }
    case 'Hyperlink': {
      const isValidUrl = (url) => {
        try {
          const parsedUrl = new URL(url);
          return ['http:', 'https:'].includes(parsedUrl?.protocol);
        } catch {
          return false;
        }
      };
      // Normalize URLs like www.example.com (no protocol) by prepending https://
      const normalizedValue =
        value && /^www\./i.test(value) ? `https://${value}` : value;
      const linkHref = isValidUrl(normalizedValue)
        ? normalizedValue
        : `https://${encodeURIComponent(value)}`;
      displayValue = value ? (
        <Link href={linkHref} target="_blank" rel="noopener noreferrer">
          {value}
        </Link>
      ) : (
        '-'
      );
      break;
    }
    case 'Association': {
      if (isShowAssociationAsTable) {
        displayValue = (
          <AssociationFieldTableViewComponent
            attribute={attributeInfo}
            entityId={entityId}
            entityData={entityData}
          />
        );
        break;
      }

      displayValue = value ? (
        <AssociationValueLink
          value={value}
          attribute={attributeInfo}
          entityData={entityData}
          formData={entityDetailsFormikForm?.values}
        />
      ) : (
        '-'
      );
      break;
    }
    case 'FieldChange':
      displayValue = value ? (
        <FieldChangeOtherDetails
          value={value}
          attribute={attributeInfo}
          mode="view"
          entityDetailsFormikForm={entityDetailsFormikForm}
          usersList={usersList}
          groupList={groupList}
        />
      ) : (
        '-'
      );
      break;
    case 'User': {
      const toLabel = (id) => findUsernameByUserId(id, usersList) || id;
      if (inputControlType === 'MULTI_SELECT' && Array.isArray(value)) {
        displayValue = value.map(toLabel).join(', ');
      } else if (value != null && value !== '') {
        displayValue = toLabel(value);
      } else {
        displayValue = '-';
      }
      break;
    }
    case 'Group': {
      const getGroup = (id) => findGroupNameByGroupId(id, groupList) || id;
      if (inputControlType === 'MULTI_SELECT' && Array.isArray(value)) {
        displayValue = value.map(getGroup).join(', ');
      } else if (value != null && value !== '') {
        displayValue = getGroup(value);
      } else {
        displayValue = '-';
      }
      break;
    }
    case 'AttributeList': {
      displayValue = value?.label ?? value?.id ?? '-';
      break;
    }
    case 'ParameterTableList': {
      displayValue = value?.name ?? value?.id ?? '-';
      break;
    }
    case 'EmailTemplateList': {
      displayValue = value?.email_template_id
        ? `${value?.email_template_id} (${value?.event_name})`
        : '-';
      break;
    }
    case 'TemplateList': {
      displayValue = value?.entity_name ? value?.entity_name : '-';
      break;
    }
    case 'FieldChangeActions': {
      displayValue = value?.key ? value?.label : '-';
      break;
    }
    default:
      // Handle special cases based on input control type
      if (inputControlType === 'IFRAME') {
        displayValue = value ? (
          <ViewChart value={value} attribute={attributeInfo} />
        ) : (
          '-'
        );
      } else if (typeof value === 'object') {
        // Convert objects to JSON string
        displayValue = JSON.stringify(value);
      }
  }

  return isValueEmpty(displayValue) ? '-' : displayValue;
};

/**
 * Check if any value is an object with key value pairs
 * Since typeof string and array also returns object
 * This function only returns true if the object has key value pairs
 * @param {any} value
 * @returns if given value is an object with key value pairs
 */
export const isObjectWithKeyValuePairs = (value) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;
